
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { HirerIdInput } from './hirer-relationships';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "hirer-relationships"
    }}>{`Hirer relationships`}</h1>
    <p>{`The SEEK API authorizes operations based on a set of relationships between you and your SEEK hirers.
Each `}<a parentName="p" {...{
        "href": "/"
      }}>{`use case`}</a>{` requires a corresponding relationship to allow you to act on behalf of the hirer:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`An `}<a parentName="p" {...{
            "href": "/use-cases/application-export"
          }}><inlineCode parentName="a">{`ApplicationExport`}</inlineCode></a>{` relationship allows you to export the hirer’s `}<a parentName="p" {...{
            "href": "/use-cases/application-export/candidate-profiles"
          }}>{`candidate profiles`}</a>{` & `}<a parentName="p" {...{
            "href": "/use-cases/application-export/attachments"
          }}>{`attachments`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`An `}<a parentName="p" {...{
            "href": "/use-cases/apply-with-seek"
          }}><inlineCode parentName="a">{`ApplicationPrefill`}</inlineCode></a>{` relationship allows you to retrieve SEEK Profile information to pre-fill an external apply form.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`A `}<a parentName="p" {...{
            "href": "/use-cases/job-posting"
          }}><inlineCode parentName="a">{`JobPosting`}</inlineCode></a>{` relationship allows you to price, post & update job ads on behalf of the hirer.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`A `}<a parentName="p" {...{
            "href": "/use-cases/proactive-sourcing"
          }}><inlineCode parentName="a">{`ProactiveSourcing`}</inlineCode></a>{` relationship allows you to `}<a parentName="p" {...{
            "href": "/use-cases/proactive-sourcing/sharing-a-candidate"
          }}>{`upload candidates`}</a>{` & `}<a parentName="p" {...{
            "href": "/use-cases/proactive-sourcing/unposted-positions"
          }}>{`unposted positions`}</a>{` belonging to the hirer.`}</p>
      </li>
    </ul>
    <p>{`Functionality shared between use cases will accept multiple relationship types.
For example, `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/position-openings"
      }}>{`position openings`}</a>{` can be managed with either a `}<inlineCode parentName="p">{`JobPosting`}</inlineCode>{` or `}<inlineCode parentName="p">{`ProactiveSourcing`}</inlineCode>{` relationship.`}</p>
    <p>{`You can view a list of your SEEK hirers and their relationships on the `}<a parentName="p" {...{
        "href": "/manage/hirers"
      }}>{`Developer Dashboard’s hirers page`}</a>{`.`}</p>
    <h2 {...{
      "id": "hirer-onboarding"
    }}>{`Hirer onboarding`}</h2>
    <p>{`SEEK must explicitly configure hirer relationships as part of onboarding a SEEK hirer to your software.
Direct hirers to the SEEK ‘Link your Software’ page for the appropriate market:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://talent.seek.com.au/partners/link-your-software/"
          }}>{`Australia`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://hk.employer.seek.com/partners/link-your-software/"
          }}>{`Hong Kong`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://id.employer.seek.com/partners/link-your-software/"
          }}>{`Indonesia`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://my.employer.seek.com/partners/link-your-software/"
          }}>{`Malaysia`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://talent.seek.co.nz/partners/link-your-software/"
          }}>{`New Zealand`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://ph.employer.seek.com/partners/link-your-software/"
          }}>{`Philippines`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://sg.employer.seek.com/partners/link-your-software/"
          }}>{`Singapore`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://th.employer.seek.com/partners/link-your-software/"
          }}>{`Thailand`}</a></p>
      </li>
    </ul>
    <p>{`GraphQL operations will fail with a `}<a parentName="p" {...{
        "href": "/graphql/error-responses#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode>{` error`}</a>{` if a required relationship hasn’t been configured.
You should contact the hirer in cases where this is unexpected.`}</p>
    <p>{`SEEK hirers in specific market segments commonly have multiple SEEK accounts aligned to divisions within their organizations.
Therefore it is important that your platform supports hirers with multiple SEEK accounts per Section B, clause 73b of the `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au/partners/terms-of-use/"
      }}>{`SEEK API Terms of Use`}</a>{`.`}</p>
    <p>{`SEEK will provide the hirer with their hirer ID for configuration in your software.
At the same time, the SEEK API will emit a `}<a parentName="p" {...{
        "href": "#events"
      }}><inlineCode parentName="a">{`HirerRelationshipChanged`}</inlineCode>{` event`}</a>{`.
While you can source the hirer ID from this event,
manual triage is still necessary to link the SEEK hirer with the relevant account in your software.`}</p>
    <p>{`If you capture the hirer ID in a self-service text field:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`The field must accept the hirer ID in full.`}</p>
        <p parentName="li">{`Per the earlier `}<a parentName="p" {...{
            "href": "/graphql/seek-api-conventions#object-identifiers"
          }}>{`SEEK API conventions`}</a>{` section,
object identifiers must not be disassembled into their component parts.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The value must be trimmed before it is passed to the SEEK API.`}</p>
        <p parentName="li">{`The SEEK API will not recognise hirer IDs with leading or trailing whitespace.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The value may be validated upfront through the SEEK API.`}</p>
        <p parentName="li">{`You can make a `}<a parentName="p" {...{
            "href": "#hiringorganization"
          }}><inlineCode parentName="a">{`hiringOrganization`}</inlineCode>{` query`}</a>{` to provide faster feedback to hirers when they enter an invalid ID.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The association must be appropriately triaged and authorized by your team.`}</p>
        <p parentName="li">{`A hirer must not be able to enter another hirer’s ID into your software and gain access to their data.`}</p>
      </li>
    </ol>

    <HirerIdInput mdxType="HirerIdInput" />
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <p>{`Before you start querying hirer relationships, you will need to `}<a parentName="p" {...{
        "href": "/auth/partner-tokens"
      }}>{`request a partner token`}</a>{`.`}</p>
    <p>{`These queries also work with a `}<a parentName="p" {...{
        "href": "/auth/browser-tokens"
      }}>{`browser token`}</a>{` but will only return the hirer the browser token is scoped to.
For the browser token to work, you will need to include the `}<inlineCode parentName="p">{`query:organizations`}</inlineCode>{` scope in your request.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <h3 {...{
      "id": "hiringorganization"
    }}><inlineCode parentName="h3">{`hiringOrganization`}</inlineCode></h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/hiringOrganization"
      }}><inlineCode parentName="a">{`hiringOrganization`}</inlineCode>{` query`}</a>{` will list your configured relationships with a SEEK hirer.
If no relationship has been configured the query will fail with a `}<a parentName="p" {...{
        "href": "/graphql/error-responses#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode>{` error`}</a>{`.`}</p>
    <p>{`The “Look up a hirer by ID” section of the `}<a parentName="p" {...{
        "href": "/manage/hirers"
      }}>{`Developer Dashboard’s hirers page`}</a>{` wraps this query in a friendly user interface.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  hiringOrganization(id: $id) {\\n    name\\n    seekApiCapabilities {\\n      relationshipTypeCodes\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":83,\"column\":1,\"offset\":4104},\"end\":{\"line\":92,\"column\":4,\"offset\":4248},\"indent\":[1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":94,\"column\":1,\"offset\":4250},\"end\":{\"line\":98,\"column\":4,\"offset\":4320},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganization\\\": {\\n    \\\"name\\\": \\\"Acme Corp\\\",\\n    \\\"seekApiCapabilities\\\": {\\n      \\\"relationshipTypeCodes\\\": [\\n        \\\"ApplicationExport\\\",\\n        \\\"ApplicationPrefill\\\",\\n        \\\"JobPosting\\\",\\n        \\\"ProactiveSourcing\\\"\\n      ]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":100,\"column\":1,\"offset\":4322},\"end\":{\"line\":114,\"column\":4,\"offset\":4577},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "hiringOrganization(id:": true,
        "$id)": true,
        "name\\n": true,
        "seekApiCapabilities": true,
        "relationshipTypeCodes\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":83,\"column\":1,\"offset\":4104},\"end\":{\"line\":92,\"column\":4,\"offset\":4248},\"indent\":[1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":94,\"column\":1,\"offset\":4250},\"end\":{\"line\":98,\"column\":4,\"offset\":4320},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hiringOrganization\\\":": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\",\\n": true,
        "\\\"seekApiCapabilities\\\":": true,
        "\\\"relationshipTypeCodes\\\":": true,
        "[\\n": true,
        "\\\"ApplicationExport\\\",\\n": true,
        "\\\"ApplicationPrefill\\\",\\n": true,
        "\\\"JobPosting\\\",\\n": true,
        "\\\"ProactiveSourcing\\\"\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":100,\"column\":1,\"offset\":4322},\"end\":{\"line\":114,\"column\":4,\"offset\":4577},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  hiringOrganization(id: $id) {
    name
    seekApiCapabilities {
      relationshipTypeCodes
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "hiringorganizations"
    }}><inlineCode parentName="h3">{`hiringOrganizations`}</inlineCode></h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/hiringOrganizations"
      }}><inlineCode parentName="a">{`hiringOrganizations`}</inlineCode>{` query`}</a>{` will return a paginated list of all SEEK hirers you have a given relationship with.`}</p>
    <p>{`The “Search all hirers” section of the `}<a parentName="p" {...{
        "href": "/manage/hirers"
      }}>{`Developer Dashboard’s hirers page`}</a>{` wraps this query in a friendly user interface.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($schemeId: String!, $relationshipTypeCodes: [String!]!, $first: Int) {\\n  hiringOrganizations(\\n    schemeId: $schemeId\\n    filter: { relationshipTypeCodes: $relationshipTypeCodes }\\n    first: $first\\n  ) {\\n    edges {\\n      node {\\n        id {\\n          value\\n        }\\n        name\\n      }\\n    }\\n    pageInfo {\\n      hasNextPage\\n      endCursor\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":122,\"column\":1,\"offset\":4848},\"end\":{\"line\":143,\"column\":4,\"offset\":5225},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n  \\\"relationshipTypeCodes\\\": [\\\"ApplicationExport\\\"],\\n  \\\"first\\\": 5\\n}\",\"position\":{\"start\":{\"line\":145,\"column\":1,\"offset\":5227},\"end\":{\"line\":151,\"column\":4,\"offset\":5340},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganizations\\\": {\\n    \\\"edges\\\": [\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n          },\\n          \\\"name\\\": \\\"Acme Corp\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:A3HL8E4w\\\"\\n          },\\n          \\\"name\\\": \\\"Contoso\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:AYAWCibZ\\\"\\n          },\\n          \\\"name\\\": \\\"Fabrikam\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:FWzEydsm\\\"\\n          },\\n          \\\"name\\\": \\\"PixelArcade Studios\\\"\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:C2p2SCBR\\\"\\n          },\\n          \\\"name\\\": \\\"Prescott Pharmaceuticals\\\"\\n        }\\n      }\\n    ],\\n    \\\"pageInfo\\\": {\\n      \\\"hasNextPage\\\": true,\\n      \\\"endCursor\\\": \\\"seekAnzPublicTest:hiringOrganizationCursor:seek:C2p2SCBR\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":153,\"column\":1,\"offset\":5342},\"end\":{\"line\":204,\"column\":4,\"offset\":6430},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($schemeId:": true,
        "String!,": true,
        "$relationshipTypeCodes:": true,
        "[String!]!,": true,
        "$first:": true,
        "Int)": true,
        "{\\n": true,
        "": true,
        "hiringOrganizations(\\n": true,
        "schemeId:": true,
        "$schemeId\\n": true,
        "filter:": true,
        "{": true,
        "relationshipTypeCodes:": true,
        "$relationshipTypeCodes": true,
        "}\\n": true,
        "first:": true,
        "$first\\n": true,
        ")": true,
        "edges": true,
        "node": true,
        "id": true,
        "value\\n": true,
        "name\\n": true,
        "pageInfo": true,
        "hasNextPage\\n": true,
        "endCursor\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":122,\"column\":1,\"offset\":4848},\"end\":{\"line\":143,\"column\":4,\"offset\":5225},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"schemeId\\\":": true,
        "\\\"seekAnzPublicTest\\\",\\n": true,
        "\\\"relationshipTypeCodes\\\":": true,
        "[\\\"ApplicationExport\\\"],\\n": true,
        "\\\"first\\\":": true,
        "5\\n}\",\"position\":{\"start\":{\"line\":145,\"column\":1,\"offset\":5227},\"end\":{\"line\":151,\"column\":4,\"offset\":5340},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hiringOrganizations\\\":": true,
        "\\\"edges\\\":": true,
        "[\\n": true,
        "\\\"node\\\":": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:A3HL8E4w\\\"\\n": true,
        "\\\"Contoso\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:AYAWCibZ\\\"\\n": true,
        "\\\"Fabrikam\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:FWzEydsm\\\"\\n": true,
        "\\\"PixelArcade": true,
        "Studios\\\"\\n": true,
        "\\\"seekAnzPublicTest:organization:seek:C2p2SCBR\\\"\\n": true,
        "\\\"Prescott": true,
        "Pharmaceuticals\\\"\\n": true,
        "],\\n": true,
        "\\\"pageInfo\\\":": true,
        "\\\"hasNextPage\\\":": true,
        "true,\\n": true,
        "\\\"endCursor\\\":": true,
        "\\\"seekAnzPublicTest:hiringOrganizationCursor:seek:C2p2SCBR\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":153,\"column\":1,\"offset\":5342},\"end\":{\"line\":204,\"column\":4,\"offset\":6430},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($schemeId: String!, $relationshipTypeCodes: [String!]!, $first: Int) {
  hiringOrganizations(
    schemeId: $schemeId
    filter: { relationshipTypeCodes: $relationshipTypeCodes }
    first: $first
  ) {
    edges {
      node {
        id {
          value
        }
        name
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "self"
    }}><inlineCode parentName="h3">{`self`}</inlineCode></h3>
    <p>{`When using a `}<a parentName="p" {...{
        "href": "/auth/browser-tokens"
      }}>{`browser token`}</a>{`, the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/self"
      }}><inlineCode parentName="a">{`self`}</inlineCode>{` query`}</a>{` will return the hirer the browser token is scoped to.
This can be used to list a hirer’s relationships directly from your frontend without having to explicitly specify the hirer’s ID.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label=\\\"Query\\\" hirerId=\\\"seekAnzPublicTest:organization:seek:9YQA3jYK\\\" scope=\\\"query:organizations\\\"\",\"value\":\"query {\\n  self {\\n    hirer {\\n      id {\\n        value\\n      }\\n      name\\n      seekApiCapabilities {\\n        relationshipTypeCodes\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":211,\"column\":1,\"offset\":6678},\"end\":{\"line\":225,\"column\":4,\"offset\":6940},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label=\\\"Result\\\"\",\"value\":\"{\\n  \\\"self\\\": {\\n    // Based on the request's browser token\\n    \\\"hirer\\\": {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:9YQA3jYK\\\"\\n      },\\n      \\\"name\\\": \\\"Vandelay Industries\\\",\\n      \\\"seekApiCapabilities\\\": {\\n        \\\"relationshipTypeCodes\\\": [\\\"ApplicationExport\\\", \\\"ApplicationPrefill\\\"]\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":227,\"column\":1,\"offset\":6942},\"end\":{\"line\":242,\"column\":4,\"offset\":7294},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":\"label": "\\\"Query\\\"",
        "hirerId": "\\\"seekAnzPublicTest:organization:seek:9YQA3jYK\\\"",
        "scope": "\\\"query:organizations\\\"\",\"value\":\"query",
        "{\\n": true,
        "": true,
        "self": true,
        "hirer": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "name\\n": true,
        "seekApiCapabilities": true,
        "relationshipTypeCodes\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":211,\"column\":1,\"offset\":6678},\"end\":{\"line\":225,\"column\":4,\"offset\":6940},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"label": "\\\"Result\\\"\",\"value\":\"{\\n",
        "\\\"self\\\":": true,
        "//": true,
        "Based": true,
        "on": true,
        "the": true,
        "request's": true,
        "browser": true,
        "token\\n": true,
        "\\\"hirer\\\":": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:9YQA3jYK\\\"\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"Vandelay": true,
        "Industries\\\",\\n": true,
        "\\\"seekApiCapabilities\\\":": true,
        "\\\"relationshipTypeCodes\\\":": true,
        "[\\\"ApplicationExport\\\",": true,
        "\\\"ApplicationPrefill\\\"]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":227,\"column\":1,\"offset\":6942},\"end\":{\"line\":242,\"column\":4,\"offset\":7294},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query {
  self {
    hirer {
      id {
        value
      }
      name
      seekApiCapabilities {
        relationshipTypeCodes
      }
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "events"
    }}>{`Events`}</h2>
    <p><inlineCode parentName="p">{`HirerRelationshipChanged`}</inlineCode>{` events are emitted whenever a relationship between you and a hirer is added or removed.
The hirer relationships in your software can be kept in sync with SEEK using these events.`}</p>
    <p>{`This is an example of a `}<a parentName="p" {...{
        "href": "/events/webhooks"
      }}>{`webhook`}</a>{` body containing a `}<inlineCode parentName="p">{`HirerRelationshipChanged`}</inlineCode>{` event:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "sample-webhook-body",
        "sample-webhook-body": true
      }}>{`{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:RJrWs6Kw13TvACTTXG6qZg",
      "type": "HirerRelationshipChanged",

      "createDateTime": "2020-10-20T23:13:58.804Z",

      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:RNzsabxEX56cuRepCD9A8j",
  "url": "https://example.com/webhook"
}
`}</code></pre>
    <p>{`Once you receive an event, you can use its `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/webhooks/HirerRelationshipChangedEvent/property/hirerId"
      }}><inlineCode parentName="a">{`hirerId`}</inlineCode></a>{` to query the current state of your relationship with that hirer from the `}<a parentName="p" {...{
        "href": "#hiringorganization"
      }}><inlineCode parentName="a">{`hiringOrganization`}</inlineCode>{` query`}</a>{`.`}</p>
    <p>{`If all relationships have been removed with the hirer, a `}<a parentName="p" {...{
        "href": "/graphql/error-responses#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode>{` error`}</a>{` will be returned.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;